import {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector as useReduxSelector } from 'react-redux';
import { useFieldArray, useForm } from 'react-hook-form';
import {
    array, object, ObjectSchema, string,
} from 'yup';
// eslint-disable-next-line import/no-extraneous-dependencies
import { v4 as uuidv4 } from 'uuid';
import { usePortfolioProcessNavigation } from 'hooks/helpers/usePortfolioProcessNavigation';
import { useYupValidationResolver } from 'hooks/useYupValidationResolver';
import {
    useCreatePortfolioActions,
    useCreatePortfolioSelectors,
    useCreatePortfolioState,
} from 'datasource/CreatePortfolio';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import {
    APPLICATION_CONTAINER_ID,
    OPTION_LIST,
    PRODUCTS,
    STATUS_OK,
    THREEBTYPES,
} from 'constants/constants';
import { notification } from 'ui-library';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import { useOptionList } from 'hooks/useOptionList';
import useCommonAllocationDataProvider from 'pages/ClientOverview/pages/Portfolios/context/useCommonAllocationDataProvider';
import { useQueryClient } from '@tanstack/react-query';
import { MAX_INVESTMENT_WITHOUT_PROOF_OF_ASSETS } from './CreatePortfolio/constants';
import { memberIdSelector } from '../redux-store/auth/authSelectors';
import { TPageError } from './PortfolioProcessLayout/PortfolioFlowLayoutManager.types';

const useShoppingCart = () => {
    const {
        t,
        i18n: { language },
    } = useTranslation();

    const queryClient = useQueryClient();

    const { setSetupWithdrawalPlan } = useCommonAllocationDataProvider();

    const [existing3bPortfolioData, setExisting3bPortfolioData] = useState({
        isBuyTransaction: true,
        transactionAmount: 2500,
        error: null,
    });
    const [cancelInProgress, setCancelInProgress] = useState<boolean>(false);
    const [continueDisabled, setContinueDisabled] = useState(true);
    const [canCheckInvestorProfile, setCanCheckInvestorProfile] = useState(false);
    const [pageErrors, setPageErrors] = useState<TPageError | undefined>(
        undefined,
    );

    const {
        selectedProductIdSelector: selectedProductId,
        selectedProductSelector,
        contactGroupIdSelector,
        isJointAccountSelector,
        currentClientSelector,
        is3aProductGroupSelector,
        is3bProductGroupSelector,
        isVbVbPremiumProductGroupSelector,
        is3aPremiumProductGroupSelector,
        isAdvisoryProcessSelector,
        advisoryUploadedDocumentSelector,
        selectedProductGroupSelector,
        advisoryIdSelector,
        advisoryDataSelector,
        currentInvestmentDescriptionSelector,
        currentSelectedStrategySelector,
        advisoryDataLoading,
        advisoryProcessInProgressSelector,
        totalPillar3aAmountSelector,
        products: productOptions,
        isLoadingProducts,
    } = useCreatePortfolioSelectors();
    const {
        saveInvestmentApplicationDataAction,
        saveCurrentInvestmentApplicationIdAction,
        startProductSetupAction,
        clearProductSetupAction,
        removeInvestmentApplicationDataAction,
        setContinueDisabledFromShoppingCartAction,
        saveInvestmentApplicationDataToStoreAction,
        setContainerApplicationsFromShoppingCart,
    } = useCreatePortfolioActions();
    const { investorProfileClientId } = useCreatePortfolioState();

    const productSettings = useMemo(
        () => selectedProductSelector?.settings,
        [selectedProductSelector?.settings],
    );
    const affiliatedToPensionFund = useMemo(
        () => currentInvestmentDescriptionSelector?.personalDetails
            ?.affiliatedToPensionFund
            || currentClientSelector?.affiliatedPensionFund,
        [
            currentInvestmentDescriptionSelector?.personalDetails
                ?.affiliatedToPensionFund,
            currentClientSelector?.affiliatedPensionFund,
        ],
    );
    const advisoryDocumentId = useMemo(
        () => advisoryUploadedDocumentSelector?.advisoryDocumentId
            ?? advisoryIdSelector,
        [
            advisoryIdSelector,
            advisoryUploadedDocumentSelector?.advisoryDocumentId,
        ],
    );
    const defaultYearlyPayment = useMemo(
        () => (affiliatedToPensionFund
            ? productSettings?.defaultYearlyContributionWithPensionFund
                ?.isDependantOnAffiliatedWithPensionFundTrue
            : productSettings?.defaultYearlyContributionWithPensionFund
                ?.isDependantOnAffiliatedWithPensionFundFalse),
        [
            affiliatedToPensionFund,
            productSettings?.defaultYearlyContributionWithPensionFund
                ?.isDependantOnAffiliatedWithPensionFundTrue,
            productSettings?.defaultYearlyContributionWithPensionFund
                ?.isDependantOnAffiliatedWithPensionFundFalse,
        ],
    );
    const minimumTransferAmountFor3ap = useMemo(
        () => (totalPillar3aAmountSelector as number >= 50000 ? 0 : productSettings?.transferAmount?.transferAmountMin),
        [productSettings?.transferAmount?.transferAmountMin, totalPillar3aAmountSelector],
    );
    const memberId = useReduxSelector(memberIdSelector);
    const containerId = useRef(
        sessionStorage.getItem(APPLICATION_CONTAINER_ID),
    )?.current;

    const isZurichEmployee = useMemo(
        () => currentClientSelector?.isZurichEmployee,
        [currentClientSelector?.isZurichEmployee],
    );
    const {
        data: existingPortfolios,
        isLoading: existingPortfoliosLoading,
        error: existingPortfoliosError,
    } = useGetCustomService({
        service: 'customAdvisoryServices',
        api: 'getExistingPortfolios',
        payload: {
            advisoryId: advisoryIdSelector || advisoryDocumentId,
            lang: language,
        },
        enabled: !!(advisoryIdSelector || advisoryDocumentId) && !!language,
        gcTime: 30 * 60 * 1000, // 30 minutes
        staleTime: 30 * 60 * 1000, // 30 minutes
        refetchOnWindowFocus: false,
    });

    const {
        data: newInvestmentPlansData,
        isLoading: newInvestmentPlansLoading,
        error: newInvestmentPlanError,
    } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'getInvestmentAppsByContainerId',
        payload: { containerId, language },
        enabled: !!containerId && containerId !== 'null' && !!language,
        gcTime: 10,
        staleTime: 10,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        setContainerApplicationsFromShoppingCart({
            applications: newInvestmentPlansData?.data,
        });
    }, [newInvestmentPlansData?.data]);

    const {
        mutateAsync: handleMutation,
        isPending: changeExisting3bPlanResponseIsPending,
    } = useGenericAPIMutation();

    const adaptNewInvestmentPlanFromAPI = useCallback(
        (data) => data
            ?.filter((dta) => dta?.status !== 2 && !dta?.changePlan)
            ?.map((plan) => ({
                id: plan?.productId,
                product: plan.productExternalId,
                productName: plan.productName,
                amount: plan?.plannedInvestmentAmount,
                newStrategy: plan?.strategy || plan?.productName,
                containerId: plan?.containerId,
                investmentApplicationId: plan?.investmentApplicationId,
                isConfigured:
                        !!plan?.investmentDescription?.agentInformation,
                yearlyPayments:
                        plan?.investmentDescription?.productConfiguration
                            ?.yearlyPayments,
                transactionAmount:
                        plan?.investmentDescription?.productConfiguration
                            ?.transactionAmount || 0,
                modelPortfolioId:
                        plan?.investmentDescription?.selectStrategy
                            ?.modelPortfolioId,
                strategyId:
                        plan?.investmentDescription?.selectStrategy?.strategyId,
            })),
        [],
    );

    const newInvestmentPlanFromAPI = useMemo(
        () => adaptNewInvestmentPlanFromAPI(newInvestmentPlansData?.data) || [],
        [newInvestmentPlansData?.data, adaptNewInvestmentPlanFromAPI],
    );

    const existing3bPortfoliosData = useMemo(
        () => existingPortfolios?.data?.portfolioWithApplicationList?.map(
            (portfolio) => {
                const isFollowUpInvestorProfile = portfolio?.changeAmountInvestmentApplication
                    ?.investmentDescription?.changeInvestmentAmount
                    ?.isFollowUpInvestorProfile;

                return {
                    id: portfolio?.id,
                    existingProduct: {
                        name: portfolio?.product?.name,
                        id: portfolio?.product?.id,
                        externalId: portfolio?.product?.externalId,
                    },
                    amount: portfolio?.currentValue,
                    newStrategy: [
                        THREEBTYPES.pk,
                        THREEBTYPES.zic,
                    ]?.includes(portfolio?.product?.externalId)
                        ? '-'
                        : portfolio?.newStrategyName,
                    isBuyTransaction:
                            !portfolio?.changeAmountInvestmentApplication
                                ?.investmentDescription?.changeInvestmentAmount
                                ?.isSell,
                    oldStrategy: portfolio?.oldStrategyName,
                    changedStrategy:
                            portfolio?.changeStrategyInvestmentApplication,
                    investmentApplication:
                            portfolio?.changeStrategyInvestmentApplication
                            || portfolio?.parentInvestmentApplication,
                    changedBuySellAmount:
                            portfolio?.changeAmountInvestmentApplication,
                    changedAmount:
                            !isFollowUpInvestorProfile
                            && portfolio?.changeAmountInvestmentApplication
                                ?.investmentDescription?.changeInvestmentAmount
                                ?.value,
                    isSell: portfolio?.changeAmountInvestmentApplication
                        ?.investmentDescription?.changeInvestmentAmount
                        ?.isSell,
                    isCaseSource:
                            portfolio?.changeAmountInvestmentApplication
                                ?.investmentDescription?.changeInvestmentAmount
                                ?.isCaseSource,
                    pendingCEPRequest: portfolio?.incomplete,
                    modelPortfolioId: portfolio?.modelPortfolioId,
                    editable:
                            ![
                                THREEBTYPES.pk,
                                THREEBTYPES.azpAlt,
                                THREEBTYPES.azpNeu,
                                THREEBTYPES.azpNeuSB,
                            ]?.includes(portfolio?.product?.externalId)
                            || false,
                    edited:
                            !isFollowUpInvestorProfile
                            && (!!portfolio?.changeAmountInvestmentApplication
                                || !!portfolio?.changeStrategyInvestmentApplication
                                || false),
                    withdrawalAmount: portfolio?.withdrawalAmount,
                    withdrawalOption:
                            portfolio?.changeStrategyInvestmentApplication
                                ? portfolio?.changeStrategyInvestmentApplication
                                    ?.investmentDescription?.withdrawal
                                : portfolio?.parentInvestmentApplication
                                    ?.investmentDescription?.withdrawal,
                    withdrawalOptionFromExistingApplication:
                            portfolio?.parentInvestmentApplication
                                ?.investmentDescription?.withdrawal,
                    paymentInstruction:
                            portfolio?.changeStrategyInvestmentApplication
                                ? portfolio?.changeStrategyInvestmentApplication
                                    ?.investmentDescription
                                    ?.paymentInstruction
                                : portfolio?.parentInvestmentApplication
                                    ?.investmentDescription
                                    ?.paymentInstruction,
                    isFollowUpInvestorProfile,
                    containerId:
                            portfolio?.changeAmountInvestmentApplication
                                ?.investmentDescription?.containerId,
                };
            },
        ),
        [existingPortfolios?.data?.portfolioWithApplicationList],
    );

    const schema: ObjectSchema = useMemo(
        () => object().shape({
            products: array().of(
                object().shape({
                    product: string().nullable(),
                    amount: string().nullable(),
                    strategy: string().nullable(),
                }),
            ),
        }),
        [t],
    );

    const resolver = useYupValidationResolver(schema);

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
        reset,
    } = useForm({
        resolver,
        defaultValues: {
            products: newInvestmentPlanFromAPI || [],
        },
    });

    const {
        fields, append, update, remove,
    } = useFieldArray({
        control,
        name: 'products',
    });

    const formData = watch();

    useEffect(() => {
        reset({ products: newInvestmentPlanFromAPI || [] });
    }, [newInvestmentPlanFromAPI]);

    const hasPKApplication = useMemo(
        () => existing3bPortfoliosData?.find(
            (data) => data?.existingProduct?.externalId === THREEBTYPES.pk,
        )?.existingProduct?.externalId === 'PK'
            || formData?.products?.find((data) => data?.product === THREEBTYPES.pk)
                ?.product === 'PK',
        [existing3bPortfoliosData, formData?.products],
    );

    useEffect(() => {
        clearProductSetupAction();
        setSetupWithdrawalPlan(false);
    }, []);

    const desiredInvestmentAmount = advisoryDataSelector?.metadata?.desiredInvestmentAmount
        || advisoryUploadedDocumentSelector?.metadata?.desiredInvestmentAmount
        || 0;
    const totalInvestmentAmountNewProducts = useMemo(
        () => (formData?.products?.length > 0
            ? formData?.products?.reduce(
                (acc, curr) => acc + (+curr.amount || 0),
                0,
            )
            : 0),
        [formData],
    );
    const totalInvestmentAmountExistingProducts = useMemo(
        () => existing3bPortfoliosData?.length > 0
            && existing3bPortfoliosData?.reduce(
                (acc, curr) => (curr?.isSell
                    ? acc - (+curr?.changedAmount || 0)
                    : acc + (+curr?.changedAmount || 0)),
                0,
            ),
        [existing3bPortfoliosData],
    );
    const totalInvestmentAmount = useMemo(
        () => totalInvestmentAmountNewProducts
            + totalInvestmentAmountExistingProducts,
        [
            totalInvestmentAmountNewProducts,
            totalInvestmentAmountExistingProducts,
        ],
    );
    const totalBuyAmount = useMemo(
        () => existing3bPortfoliosData
            ?.filter((portfolio) => portfolio?.isBuyTransaction)
            ?.reduce((acc, curr) => acc + (+curr?.changedAmount || 0), 0),
        [existing3bPortfoliosData],
    );
    const remainingInvestmentAmount = useMemo(
        () => desiredInvestmentAmount - totalInvestmentAmount,
        [desiredInvestmentAmount, totalInvestmentAmount],
    );
    const isProofOfAssetsNeeded = useMemo(
        () => is3bProductGroupSelector
            && totalInvestmentAmountNewProducts + (totalBuyAmount || 0)
                >= MAX_INVESTMENT_WITHOUT_PROOF_OF_ASSETS,
        [
            totalInvestmentAmountNewProducts,
            totalBuyAmount,
            is3bProductGroupSelector,
        ],
    );
    const productOptions3B = useMemo(
        () => productOptions
            .filter((f) => f.settings.productKey === PRODUCTS.bbb)
            ?.map((product) => {
                const productToSave = { ...product };

                if (
                    (hasPKApplication
                            && productToSave.externalId === THREEBTYPES.pk)
                        || (!isAdvisoryProcessSelector
                            && (productToSave.externalId === THREEBTYPES.azpNeu
                                || productToSave.externalId
                                    === THREEBTYPES.azpNeuSB
                                || productToSave.externalId === THREEBTYPES.zivv))
                ) {
                    productToSave.isEnabled = false;
                }

                const minOneTimeInvestmentFor3B = isZurichEmployee
                    ? productToSave?.settings?.minOneTimeInvestmentZurich
                          ?? productToSave?.settings?.minOneTimeInvestment
                    : productToSave?.settings?.minOneTimeInvestment;

                return {
                    label: productToSave?.name,
                    value: productToSave?.id,
                    externalId: productToSave?.externalId,
                    minimumInvestment: minOneTimeInvestmentFor3B,
                    disabled:
                            !productToSave?.isEnabled
                            || (productToSave.externalId !== THREEBTYPES.pk
                                && isAdvisoryProcessSelector
                                && remainingInvestmentAmount <= 0),
                };
            }),
        [
            productOptions,
            hasPKApplication,
            isAdvisoryProcessSelector,
            remainingInvestmentAmount,
        ],
    );

    const getDefaultAmountBasedOnProduct = () => {
        if (is3bProductGroupSelector) return null;
        if (is3aPremiumProductGroupSelector) return minimumTransferAmountFor3ap;

        return defaultYearlyPayment;
    };

    const handleAddProduct = useCallback(() => {
        append({
            product: is3bProductGroupSelector
                ? null
                : selectedProductSelector?.externalId,
            amount: getDefaultAmountBasedOnProduct(),
        });
    }, [
        is3bProductGroupSelector,
        defaultYearlyPayment,
        selectedProductSelector?.externalId,
        getDefaultAmountBasedOnProduct,
    ]);

    const cancelInvestmentApplication = useCallback(
        async (index, applicationId) => {
            try {
                setCancelInProgress(applicationId);
                const response: any = await handleMutation({
                    service: 'customInvestmentService',
                    API: 'deleteInvestmentApplication',
                    payload: { investmentApplicationId: applicationId },
                });

                if (response?.status === STATUS_OK) {
                    remove(index);
                    removeInvestmentApplicationDataAction({
                        id: applicationId,
                    });
                    notification.open({
                        content: t(
                            'dashboard.overviewClientApplications.canceledSuccessfully',
                        ),
                        type: 'success',
                    });
                }
            } catch (err) {
                setPageErrors({
                    message: t(
                        'dashboard.overviewClientApplications.cancelError',
                    ),
                });
                notification.open({
                    content: t(
                        'dashboard.overviewClientApplications.cancelError',
                    ),
                    type: 'error',
                });
            } finally {
                setCancelInProgress(false);
            }
        },
        [t],
    );

    const handleDeleteRow = useCallback(({ index, mode, applicationId }) => {
        if (mode === 'editMode') {
            remove(index);
        } else {
            cancelInvestmentApplication(index, applicationId);
        }
    }, []);

    const createNewInvestmentPlan = useCallback(
        async (data, index) => {
            const newInvestmentPlanData = data?.products?.[index];
            const productId = !is3bProductGroupSelector
                ? selectedProductSelector?.id
                : +newInvestmentPlanData?.product;
            const externalId = !is3bProductGroupSelector
                ? selectedProductSelector?.externalId
                : productOptions3B?.find(
                    (product) => product?.value === +newInvestmentPlanData?.product,
                )?.externalId;
            const cId = sessionStorage.getItem(APPLICATION_CONTAINER_ID);

            const uuid = uuidv4();

            if (!cId || cId === 'null') {
                sessionStorage.setItem(APPLICATION_CONTAINER_ID, uuid);
            }

            try {
                const payload = {
                    contactId: currentClientSelector?.id,
                    memberId,
                    contactGroupId: contactGroupIdSelector,
                    isJointAccount: isJointAccountSelector,
                    productId,
                    productExternalId: externalId,
                    advisoryDocumentId,
                    plannedInvestment: +newInvestmentPlanData?.amount,
                    containerId: cId && cId !== 'null' ? cId : uuid,
                    ...(isJointAccountSelector
                        ? {
                            investorProfileContactId: investorProfileClientId,
                        }
                        : {}),
                };

                const additionalData = {
                    product: selectedProductSelector,
                };

                const result = await saveInvestmentApplicationDataAction({
                    payload,
                    method: 'saveProductDetails',
                    additionalData,
                });

                if (result?.data?.investmentDescription?.containerId) {
                    const plan = result?.data?.investmentDescription;
                    const newData = {
                        id: plan?.productId,
                        product: plan.productExternalId,
                        productName: productOptions?.find(
                            (product) => product?.id === plan?.productId,
                        )?.name,
                        amount: plan?.productConfiguration?.initialInvestment,
                        newStrategy: plan?.strategy || plan?.productName,
                        containerId: plan?.containerId,
                        investmentApplicationId:
                            result?.data?.investmentApplicationId,
                        isConfigured:
                            !!plan?.investmentDescription?.agentInformation,
                        yearlyPayments:
                            plan?.investmentDescription?.productConfiguration
                                ?.yearlyPayments,
                        transactionAmount:
                            plan?.investmentDescription?.productConfiguration
                                ?.transactionAmount || 0,
                        modelPortfolioId:
                            plan?.investmentDescription?.selectStrategy
                                ?.modelPortfolioId,
                        strategyId:
                            plan?.investmentDescription?.selectStrategy
                                ?.strategyId,
                    };

                    update(index, newData);
                }
            } catch (e: any) {
                setPageErrors({
                    message:
                        e?.message
                        || e
                        || t('dashboard.overviewClientApplications.cancelError'),
                });
            }
        },
        [
            selectedProductSelector,
            contactGroupIdSelector,
            isJointAccountSelector,
            currentClientSelector?.id,
            memberId,
            selectedProductId,
            selectedProductGroupSelector,
            productOptions3B,
            is3bProductGroupSelector,
            isVbVbPremiumProductGroupSelector,
            is3aProductGroupSelector,
            advisoryDocumentId,
            investorProfileClientId,
        ],
    );

    const saveNewProduct = async (data, index) => {
        await createNewInvestmentPlan(data, index);
    };

    const handleConfig = useCallback(
        (id: number) => {
            if (id) {
                startProductSetupAction(id);

                stepComplete();
            }
        },
        [startProductSetupAction],
    );

    useEffect(() => {
        const hasChangedExistingPlan = existingPortfolios?.data?.portfolioWithApplicationList?.some(
            (plan) => plan?.changeStrategyInvestmentApplication
                    || plan?.changeAmountInvestmentApplication,
        );
        const hasNoProducts = formData?.products?.length < 1 && !hasChangedExistingPlan;
        const hasNegativeInvestmentAmountWithAdvisory = !!advisoryIdSelector && remainingInvestmentAmount < 0;
        const hasNonConfiguredPlan = formData?.products?.length
            && formData?.products?.find((plan) => !plan?.isConfigured);

        setCanCheckInvestorProfile(hasNoProducts);
        setContinueDisabled(
            hasNoProducts
                || hasNegativeInvestmentAmountWithAdvisory
                || !!hasNonConfiguredPlan,
        );
    }, [
        formData?.products,
        remainingInvestmentAmount,
        existingPortfolios?.data?.portfolioWithApplicationList,
    ]);

    const { stepComplete } = usePortfolioProcessNavigation({
        enableAutoStepComplete: true,
        backDisabled: !advisoryProcessInProgressSelector,
        continueDisabled,
        pageErrors,
    });

    const changeExisting3bPlan = useCallback(
        async ({ requestType, API, payload }) => {
            const additionalPayload = requestType === 'post' ? { advisoryDocumentId } : {};

            try {
                await handleMutation({
                    service: 'customInvestmentService',
                    API,
                    payload: { ...payload, ...additionalPayload },
                });
            } catch (error: any) {
                setPageErrors({
                    message:
                        error?.message
                        || error
                        || `${t('contactGroups.somethingWentWrong')}`,
                });
            }
        },
        [],
    );

    useEffect(() => {
        setContinueDisabledFromShoppingCartAction(
            continueDisabled ? 'true' : 'false',
        );
    }, [continueDisabled]);

    useEffect(() => {
        if (newInvestmentPlanError || existingPortfoliosError) {
            setPageErrors({
                message:
                    newInvestmentPlanError?.message
                    || existingPortfoliosError?.message
                    || '',
            });
        }
    }, [newInvestmentPlanError, existingPortfoliosError]);

    const {
        lists: [
            optionPreferenceStock = [],
            optionPreferenceExclusion = [],
            optionPreferenceInclusion = [],
        ],
    } = useOptionList([
        OPTION_LIST.preferenceStocks,
        OPTION_LIST.preferenceExclusion,
        OPTION_LIST.preferenceInclusion,
    ]);

    const checkInvestorProfileActive = useMemo(() => existing3bPortfoliosData?.some(
        (portfolio) => portfolio?.isFollowUpInvestorProfile,
    ), [existing3bPortfoliosData]);

    const [fakeLoading, setFakeLoading] = useState(false);

    const handleCheckInvestorProfile = useCallback(
        async ({ portfolioId }: { portfolioId: number }) => {
            setFakeLoading(true);
            const uuid = uuidv4();
            const response: any = await handleMutation({
                service: 'customInvestmentService',
                API: 'createFakeInvestorProfileCheckingApplication',
                payload: {
                    portfolioId,
                    advisoryDocumentId,
                    containerId: uuid,
                },
            });

            saveInvestmentApplicationDataToStoreAction({
                payload: {
                    ...response?.data,
                    changePlan: true,
                    isFollowUpInvestorProfile: true,
                },
            });

            setFakeLoading(false);
            await queryClient.invalidateQueries({ queryKey: ['getData_getExistingPortfolios'] });
        },
        [advisoryDocumentId, queryClient],
    );

    useEffect(() => {
        const fakeApplicationContainerId = existing3bPortfoliosData?.find(
            (portfolio) => portfolio?.containerId,
        )?.containerId;

        if (fakeApplicationContainerId) {
            sessionStorage.setItem(
                APPLICATION_CONTAINER_ID,
                fakeApplicationContainerId,
            );
        }
    }, [existing3bPortfoliosData]);

    const cancelInvestorProfileCheck = useCallback(async () => {
        setFakeLoading(true);
        const fakeApplication = existing3bPortfoliosData?.find(
            (portfolio) => portfolio?.containerId,
        );

        const fakeApplicationContainerId = fakeApplication?.containerId;

        await handleMutation({
            service: 'customInvestmentService',
            API: 'cancelFakeInvestorProfileCheckingApplication',
            payload: {
                containerId: fakeApplicationContainerId,
            },
        });

        removeInvestmentApplicationDataAction({
            id: fakeApplication?.changedBuySellAmount?.investmentApplicationId,
        });
        sessionStorage.removeItem(APPLICATION_CONTAINER_ID);
        setFakeLoading(false);
        await queryClient.invalidateQueries({ queryKey: ['getData_getExistingPortfolios'] });
    }, [existing3bPortfoliosData, queryClient, removeInvestmentApplicationDataAction]);

    return {
        formData,
        existingPortfolios: {
            data: existing3bPortfoliosData,
            error: existingPortfoliosError,
            loading: existingPortfoliosLoading || false,
        },
        advisoryId: advisoryIdSelector,
        desiredInvestmentAmount,
        newProductsLoading: newInvestmentPlansLoading,
        existing3bPortfolioData,
        setExisting3bPortfolioData,
        totalInvestmentAmount,
        remainingInvestmentAmount,
        fields,
        control,
        errors,
        handleDeleteRow,
        handleAddProduct,
        saveNewProduct: handleSubmit(saveNewProduct),
        setContinueDisabled,
        productOptions3B,
        cancelInProgress,
        handleConfig,
        is3bProductGroupSelector,
        isVbVbPremiumProductGroupSelector,
        is3aPremiumProductGroupSelector,
        is3aProductGroupSelector,
        selectedProductSelector,
        isAdvisoryProcessSelector,
        currentClientSelector,
        advisoryUploadedDocumentSelector,
        saveCurrentInvestmentApplicationIdAction,
        continueDisabled,
        changeExisting3bPlan,
        changeExisting3bPlanResponseIsPending,
        selectedStrategyId: currentSelectedStrategySelector?.Id,
        advisoryDocumentId,
        advisoryDataLoading,
        isProofOfAssetsNeeded,
        hasPKApplication,
        isZurichEmployee,
        saveInvestmentApplicationDataToStoreAction,
        removeInvestmentApplicationDataAction,
        optionPreferenceStock,
        optionPreferenceExclusion,
        optionPreferenceInclusion,
        canCheckInvestorProfile,
        handleCheckInvestorProfile,
        fakeLoading,
        checkInvestorProfileActive,
        cancelInvestorProfileCheck,
        isLoadingProducts,
    };
};

export default useShoppingCart;
